import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaCalendarAlt } from 'react-icons/fa';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Calendar Component
const CalendarComponent = () => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Dummy events for demonstration
  const events = [
    { date: new Date(currentDate.getFullYear(), currentDate.getMonth(), 15), title: 'Alumni Meetup' },
    { date: new Date(currentDate.getFullYear(), currentDate.getMonth(), 22), title: 'Career Fair' },
  ];

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center">
          <FaCalendarAlt className="mr-2 text-blue-500" />
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h2>
        <div className="flex space-x-2">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={prevMonth}
            className="p-2 rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300"
          >
            <FaChevronLeft />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={nextMonth}
            className="p-2 rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300"
          >
            <FaChevronRight />
          </motion.button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-2">
        {dayNames.map(day => (
          <div key={day} className="text-center font-semibold text-gray-600">
            {day}
          </div>
        ))}
        {[...Array(firstDayOfMonth).keys()].map(i => (
          <div key={`empty-${i}`} className="h-10"></div>
        ))}
        {[...Array(daysInMonth).keys()].map(i => {
          const day = i + 1;
          const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
          const event = events.find(e => e.date.getDate() === day);
          return (
            <motion.div
              key={`day-${day}`}
              whileHover={{ scale: 1.1 }}
              className={`h-10 flex items-center justify-center rounded-full cursor-pointer
                ${event ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-100'}`}
            >
              {day}
              {event && <div className="w-1 h-1 bg-blue-500 rounded-full absolute bottom-1"></div>}
            </motion.div>
          );
        })}
      </div>
      <div className="mt-4">
        <h3 className="font-semibold text-gray-700 mb-2">Upcoming Events:</h3>
        <ul className="space-y-1">
          {events.map((event, index) => (
            <li key={index} className="text-sm text-gray-600">
              {event.date.getDate()} {monthNames[event.date.getMonth()]}: {event.title}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// Referral Statistics Chart Component
const ReferralStatisticsChart = () => {
  const data = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Referrals Sent',
        data: [12, 19, 3, 5, 2, 3],
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
      },
      {
        label: 'Referrals Accepted',
        data: [8, 15, 2, 4, 1, 2],
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Referral Statistics',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Referrals',
        },
      },
    },
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <Line data={data} options={options} />
      <div className="mt-4">
        <h3 className="font-semibold text-gray-700 mb-2">Key Metrics:</h3>
        <ul className="space-y-1 text-sm text-gray-600">
          <li>Total Referrals Sent: 44</li>
          <li>Total Referrals Accepted: 32</li>
          <li>Acceptance Rate: 72.7%</li>
        </ul>
      </div>
    </div>
  );
};

export { CalendarComponent, ReferralStatisticsChart };